import React, { useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import ConfirmUnclaimDialog from "@components/ConfirmUnclaimDialog/ConfirmUnclaimDialog";
import FlowActionButton from "@components/FlowActionButtons/FlowActionButton";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import type { BayHireDetailDrawerName } from "@features/bay-hire/hooks/useBayHireDetailActions";
import type { BayHireRequest } from "@features/bay-hire/types";
import { useTranslation } from "react-i18next";

const LANG_BASE = "bay-hire";

interface BayHireFlowButtonsProps {
  request: BayHireRequest;
  /** Gates the entire row: renders nothing for non-Operations users. */
  isOperationsUser: boolean;
  /** True while a direct claim/unclaim/verify mutation is in flight. */
  inFlight: boolean;
  onClaim: () => void;
  onUnclaim: () => void;
  onVerifyPayment: () => void;
  openDrawer: (name: BayHireDetailDrawerName) => void;
}

/**
 * DFT-0027 (Slice B2): Operations lifecycle action row for the routed Request
 * Detail header. Structure mirrors LicenseFlowButtons.tsx - a flat flex row of
 * status-aware buttons. Gating replicates the operations queue's DFT-0034 task-
 * status predicates (currentTaskStatus READY/CLAIMED + request status), which
 * gate purely on task status (no per-action permission and no claim-owner check
 * - backend enforcement is authoritative, matching queue parity).
 *
 * Direct actions (Claim/Unclaim/Verify Payment) call handlers that mutate then
 * refetch. Dialog actions (Pre-approve/Reject/Add to Waiting List/Reject
 * Payment) open MUI dialogs via `openDrawer`.
 *
 * DFT-0044 (FE-55 area, AC39 decision) originally gave Operations NO Cancel action - Cancel was
 * exclusively a Shipping Agent (vendor) capability. Megaplan (2026-08-05, item 8) REVERSES that
 * decision at the requester's explicit instruction: Operations can now cancel a request they have
 * CLAIMED, from REQUESTED or WAITING_LIST (`canCancel` below) - this actually restores what
 * `05_Port_License_details-18.png` (PoF Visuals_Bay Hire 2) always showed in the Ops row, which
 * AC39 had overridden. The vendor's own Cancel control (BayHireRequestDetail.tsx header, list
 * kebab) is unchanged and independent - both surfaces call the same BFF `:cancel` endpoint, which
 * already branches on caller role server-side.
 *
 * DFT-0044 (FE-55, AC39 decision, `Payment done` row): "Verify Payment" is relabelled "Approve"
 * and "Reject Payment" converges onto the same "Reject" label the other statuses use - copy only,
 * neither the handlers (`onVerifyPayment`) nor the underlying BFF calls change (`:verify-payment`
 * and `:reject-payment` remain distinct endpoints from `:reject` - AD-25's domain reject() is
 * shared, the HTTP paths are not). testIds are kept stable (`-verify-payment-button`,
 * `-reject-payment-button`) since they are internal identifiers, not user-facing copy.
 *
 * DFT-0044 (FE-59): `05_Port_License_details-7.png` (PoF Visuals_Bay Hire 2) shows a "Confirm Bay
 * Hire Approval" step between clicking "Approve" and the request actually being approved - Approve
 * now opens that confirm first (GenericDialog, matching Gatepass's ApproveGatepassDrawer's own
 * click-Submit -> confirm-dialog -> actually-mutate two-step) rather than calling
 * `onVerifyPayment` directly.
 *
 * DFT-0044 (order/styling review, `05_Port_License_details-18.png`/`-7.png`): two fixes, checked
 * against BOTH wireframe screens AND the platform's own convention before applying (Gatepass's
 * ApproveGatepass/RejectGatepass and Movement's ApproveMovement/RejectMovement - neither uses
 * `color="success"`/`color="error"`, both plain default/primary `contained`/`outlined`):
 * - Pre-approve/Approve (the primary CTA for their own row) now render LAST, not first - both
 *   wireframe screens show the "go forward" action trailing Reject/Add to Waiting List, not
 *   leading them.
 * - Removed `color="success"` (Pre-approve/Approve) and `color="error"` (Reject/Reject Payment) -
 *   neither wireframe screen uses semantic red/green for these buttons (all plain blue,
 *   outlined/contained only), and neither does Gatepass/Movement's own equivalent, so this was a
 *   Bay Hire-only divergence from an established convention, not a design choice to preserve.
 *
 * DFT-0044 (size fix, bug report "buttons are smaller than other feature buttons"): removed
 * `size="small"` from every button here too - `FlowActionButton` (Gatepass's own Claim/Approve/
 * Reject buttons, `@components/FlowActionButtons/FlowActionButton.tsx`) never sets a `size` prop
 * at all, so the MUI default (medium) is the platform convention this feature had diverged from.
 * Same fix applied to the vendor header's Cancel Request/Update Request buttons
 * (BayHireRequestDetail.tsx).
 *
 * DFT-0100: every button here now renders via the shared `FlowActionButton` (the same component
 * Gatepass/Movement/Licensing use for their own lifecycle action rows), instead of a raw MUI
 * `Button`. Uses the BASE component directly rather than any of the named wrappers
 * (ClaimButton/RejectButton/etc.) - every one of those hardcodes its own `-button`-suffixed
 * labelKey (e.g. `claim-button`), and NONE of those keys exist under the `bay-hire` namespace
 * (verified against `public/locales/en.json`) - reusing a wrapper verbatim would hit a missing-
 * translation fallback. Bay-hire's own existing un-suffixed keys (`claim`, `unclaim`,
 * `reject-action`, etc.) are passed directly instead. `permission` is omitted for every action -
 * this row's gating is entirely `isActionAvailable` (task-status predicates), matching the
 * pre-DFT-0100 behavior of no per-action permission check.
 *
 * Unclaim gains a confirmation step it did not have before (new behavior, not just a component
 * swap) via the shared `ConfirmUnclaimDialog` - mirrors `gatepass`'s `UnclaimGatepass.tsx`
 * precedent, whose `confirmAction` fires the existing `onUnclaim` mutation.
 */
const BayHireFlowButtons: React.FC<BayHireFlowButtonsProps> = ({
  request,
  isOperationsUser,
  inFlight,
  onClaim,
  onUnclaim,
  onVerifyPayment,
  openDrawer,
}) => {
  const { t } = useTranslation();
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showUnclaimConfirm, setShowUnclaimConfirm] = useState(false);

  if (!isOperationsUser) return null;

  const isReady = request.currentTaskStatus === "READY";
  const isClaimed = request.currentTaskStatus === "CLAIMED";
  // Bug fix (Ops post-cancel stale Unclaim -> 409): unlike every other action in this row,
  // Unclaim used to gate on `isClaimed` alone, with no `request.status` check. Reject/cancel/
  // pre-approve/verify all complete the CLAIMED task server-side as part of their own transition
  // (TM-callback pathway - see DFT-0010's technical-approach AD-03), so once the request reaches a
  // resolved/terminal status (APPROVED/REJECTED/CANCELLED) there is no longer a live task to
  // unclaim - only a stale `currentTaskStatus: "CLAIMED"` left over from the pre-mutation read
  // that this row hasn't refetched away yet. Clicking Unclaim there 409s (BFF: no CLAIMED task
  // owned by the caller). Matches canPreApprove/canReject/canCancel's own pattern of never
  // trusting `isClaimed` alone once a terminal status is reached.
  const canUnclaim =
    isClaimed &&
    request.status !== "APPROVED" &&
    request.status !== "REJECTED" &&
    request.status !== "CANCELLED";
  // 🔴 DFT-0067 bug fix: Claim used to render on `isReady` ALONE - the only action in this row
  // that did not also check request.status, and the same oversight canUnclaim above was already
  // fixed for. After a Shipping Agent cancelled a PAYED request, Operations was still shown Claim
  // on the (correctly) Cancelled request, and clicking it SUCCEEDED silently: the underlying
  // payment-verification Task Management task was genuinely still READY, because the worker's
  // cleanupOrphanedTask searched only the "bay-hire-{id}" businessProcessType generation and
  // missed the "bay-hire-payment-{id}" one (fixed in bay-hire-workflow-worker). Ops therefore took
  // ownership of work that no longer existed.
  //
  // Same terminal-status exclusion as canUnclaim on purpose, rather than an allow-list of Ongoing
  // statuses: two subtly different notions of "terminal" in one component is how this class of bug
  // survives. Backend now refuses it too (bff-bay-hire-service actOnActiveTask) - this guard is the
  // outer layer, not the only one.
  const canClaim =
    isReady &&
    request.status !== "APPROVED" &&
    request.status !== "REJECTED" &&
    request.status !== "CANCELLED";
  // DFT-0044 (FE-53): mirrors BayHireRequestStatus.ALLOWED_TRANSITIONS exactly - only REQUESTED
  // and WAITING_LIST transition to PRE_APPROVED. The previous `status !== "PAYED"` form let
  // Pre-approve render (and fail with a domain 400) at PRE_APPROVED/APPROVED/REJECTED/CANCELLED
  // too, wherever a claimed task somehow still existed - a state normal workflow operation should
  // never produce, but not one the UI should trust to stay that way.
  // DFT-0084: EXEMPTED requests never enter PRE_APPROVED for a new decision - Pre-approve is
  // hidden for them (not merely disabled), same treatment canAddToWaitingList below already
  // gives EXEMPTED for a different reason (DFT-0044 FE-58).
  const canPreApprove =
    isClaimed &&
    (request.status === "REQUESTED" || request.status === "WAITING_LIST") &&
    request.paymentType !== "EXEMPTED";
  // DFT-0084: the EXEMPTED-only direct-approve action - REQUESTED/WAITING_LIST straight to
  // APPROVED, never entering PRE_APPROVED. Mirrors canPreApprove's own status gating exactly,
  // just restricted to EXEMPTED instead of excluding it.
  const canApproveExempted =
    isClaimed &&
    (request.status === "REQUESTED" || request.status === "WAITING_LIST") &&
    request.paymentType === "EXEMPTED";
  // DFT-0044 (FE-53): Reject has its OWN, wider transition set than Pre-approve - the domain also
  // allows PRE_APPROVED -> REJECTED (a claimed task can legitimately exist there if Operations
  // reopens the case before the vendor pays), which the previous shared `canPreApprove` guard
  // incorrectly hid this button under. PAYED is excluded here because it has its own "Reject
  // Payment" button below (same domain reject() call, AD-25) - showing both would duplicate it.
  const canReject =
    isClaimed &&
    (request.status === "REQUESTED" ||
      request.status === "PRE_APPROVED" ||
      request.status === "WAITING_LIST");
  // Not simply canPreApprove: the domain allows WAITING_LIST -> {PRE_APPROVED,
  // REJECTED, CANCELLED} only, so adding an already-deferred request to the
  // waiting list again is invalid. It also cannot fail safely - the BFF
  // completes the Task Management task before the domain rejects the
  // transition, leaving the request stranded (still WAITING_LIST, no active
  // task, failed workflow). Promote/reject/cancel stay available.
  //
  // DFT-0044 (FE-58, AC13): also hidden - not disabled - for EXEMPTED requests. Deferring an
  // Exempted request would sit it in the queue with no fee to motivate resolution, and D27's
  // server-side 409 guard means offering the button here would only produce a failed click.
  const canAddToWaitingList =
    canPreApprove && request.status !== "WAITING_LIST" && request.paymentType !== "EXEMPTED";
  const canVerify = isClaimed && request.status === "PAYED";
  // Megaplan (2026-08-05, item 8): Operations can now cancel a request they have CLAIMED, from
  // REQUESTED or WAITING_LIST - this REVERSES DFT-0044's AC39 decision (Cancel used to be
  // exclusively a Shipping Agent action) at the requester's explicit instruction. Deliberately
  // narrower than canReject's status set (PRE_APPROVED/PAYED excluded - only the two statuses
  // explicitly asked for). Requires a claim first, matching every other action in this row - the
  // BFF's Ops-path cancel endpoint 409s without one (`cancelViaOperationsPath`'s claimed-task
  // precondition), so an unclaimed request is deliberately out of scope here, not merely unhandled.
  const canCancel =
    isClaimed && (request.status === "REQUESTED" || request.status === "WAITING_LIST");

  return (
    <div className="flex items-center gap-2" data-testid="bay-hire-detail-flow-buttons">
      {/* FlowActionButton has no disabled/pending prop of its own (unlike the hand-rolled Button
          this replaces) - every direct-action onClick here is guarded inline against `inFlight`
          instead, preserving the double-submission protection the old `disabled={inFlight}` gave,
          even though the button no longer looks visually disabled while a mutation is in flight
          (matches every other FlowActionButton-based flow row in the codebase - none of them
          disable during in-flight mutations either, relying on the row re-rendering once the
          mutation's refetch lands instead). */}
      <FlowActionButton
        langBase={LANG_BASE}
        isActionAvailable={canClaim}
        labelKey={inFlight ? "claiming" : "claim"}
        testId="bay-hire-detail-claim-button"
        variant="contained"
        startIcon={<ContentPasteGoIcon />}
        onClick={() => !inFlight && onClaim()}
      />

      <FlowActionButton
        langBase={LANG_BASE}
        isActionAvailable={canUnclaim}
        labelKey={inFlight ? "unclaiming" : "unclaim"}
        testId="bay-hire-detail-unclaim-button"
        variant="outlined"
        onClick={() => !inFlight && setShowUnclaimConfirm(true)}
      />

      {/* Megaplan (2026-08-05, item 8): new for Operations - no wireframe precedent for its exact
          position among this row's other buttons, so placed adjacent to Reject (both are "stop"
          actions), after Unclaim, before Reject. */}
      <FlowActionButton
        langBase={LANG_BASE}
        isActionAvailable={canCancel}
        labelKey="cancel-request"
        testId="bay-hire-detail-cancel-button"
        variant="outlined"
        startIcon={<CancelOutlinedIcon />}
        onClick={() => openDrawer("cancel")}
      />

      <FlowActionButton
        langBase={LANG_BASE}
        isActionAvailable={canReject}
        labelKey="reject-action"
        testId="bay-hire-detail-reject-button"
        variant="outlined"
        startIcon={<CancelOutlinedIcon />}
        onClick={() => openDrawer("reject")}
      />

      <FlowActionButton
        langBase={LANG_BASE}
        isActionAvailable={canAddToWaitingList}
        labelKey="add-to-waiting-list-action"
        testId="bay-hire-detail-add-to-waiting-list-button"
        variant="outlined"
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => openDrawer("addToWaitingList")}
      />

      <FlowActionButton
        langBase={LANG_BASE}
        isActionAvailable={canPreApprove}
        labelKey="pre-approve-action"
        testId="bay-hire-detail-pre-approve-button"
        variant="contained"
        startIcon={<ContentPasteGoIcon />}
        onClick={() => openDrawer("preApprove")}
      />

      {/* DFT-0084: EXEMPTED's direct approve - reuses the "Approve" copy PAYED's own button
          already uses (FE-55 precedent), same position Pre-approve occupies for other payment
          types (the primary CTA for this row, trailing Reject/Add to Waiting List). */}
      <FlowActionButton
        langBase={LANG_BASE}
        isActionAvailable={canApproveExempted}
        labelKey="approve-action"
        testId="bay-hire-detail-approve-exempted-button"
        variant="contained"
        startIcon={<ContentPasteGoIcon />}
        onClick={() => openDrawer("approveExempted")}
      />

      {/* AD-25: same domain reject() as the Reject button above; distinct HTTP path
          (:reject-payment vs :reject) and testId - both kept, copy converges on "Reject". */}
      <FlowActionButton
        langBase={LANG_BASE}
        isActionAvailable={canVerify}
        labelKey="reject-action"
        testId="bay-hire-detail-reject-payment-button"
        variant="outlined"
        startIcon={<CancelOutlinedIcon />}
        onClick={() => openDrawer("rejectPayment")}
      />

      <FlowActionButton
        langBase={LANG_BASE}
        isActionAvailable={canVerify}
        labelKey={inFlight ? "approving" : "approve-action"}
        testId="bay-hire-detail-verify-payment-button"
        variant="contained"
        startIcon={<ContentPasteGoIcon />}
        onClick={() => !inFlight && setShowApproveConfirm(true)}
      />

      <GenericDialog
        open={showApproveConfirm}
        testId="bay-hire-confirm-approval-dialog"
        title={t("bay-hire.confirm-approval-title")}
        elementContent={t("bay-hire.confirm-approval-message")}
        cancelButtonText={t("common.cancel", { defaultValue: "Cancel" })}
        confirmButtonText={t("bay-hire.approve-action")}
        handleCloseDialog={() => setShowApproveConfirm(false)}
        confirmAction={() => {
          setShowApproveConfirm(false);
          onVerifyPayment();
        }}
        isSubmitting={inFlight}
      />

      <ConfirmUnclaimDialog
        isOpen={showUnclaimConfirm}
        setIsOpen={setShowUnclaimConfirm}
        languageBase={LANG_BASE}
        itemId={request.requestNo ?? request.id ?? ""}
        confirmAction={() => {
          setShowUnclaimConfirm(false);
          onUnclaim();
        }}
        testId="bay-hire-unclaim-confirm-dialog"
      />
    </div>
  );
};

export default BayHireFlowButtons;
